import type { EmailAccount } from '../../../../api/email-accounts';
import type {
  TaskDefinition,
  TaskParameterDescription,
} from '../../../../api/types';

export type InspectorVisibilityRule = {
  show?: Record<string, unknown>;
  hide?: Record<string, unknown>;
};

export type InspectorFieldDescription = TaskParameterDescription & {
  placeholder?: string;
  rows?: number;
  maxRows?: number;
  typeOptions?: {
    rows?: number;
  };
  visibility?: InspectorVisibilityRule[];
};

export type InspectorFieldEntries = Array<[string, InspectorFieldDescription]>;
export type InspectorFieldOption =
  | string
  | number
  | {
      value?: unknown;
      label?: unknown;
      description?: unknown;
    };

export function isSensitiveKeyName(key: string): boolean {
  const normalized = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

  return (
    /^(password|passphrase|secret|private_key|secret_key)$/i.test(normalized) ||
    /\bapi[_-]?key\b/i.test(normalized) ||
    /\bsecret[_-]?access[_-]?key\b/i.test(normalized) ||
    /\b(access|refresh|bearer)[_-]?token\b/i.test(normalized) ||
    /\bclient[_-]?secret\b/i.test(normalized) ||
    /\baccess[_-]?key[_-]?id\b/i.test(normalized)
  );
}

export function isSecretField(description?: TaskParameterDescription): boolean {
  const component = description?.component?.toLowerCase();
  return component === 'secret' || component === 'password';
}

export function parseConfigValue(val: unknown, types?: string[]): unknown {
  if (types?.includes('boolean')) {
    return typeof val === 'boolean' ? val : val === 'true';
  }
  if (types?.includes('int') || types?.includes('float')) {
    const num = Number(val);
    return Number.isNaN(num) ? val : num;
  }
  const acceptsString = types?.includes('string') ?? false;
  const acceptsObject = types?.includes('object') ?? false;
  const acceptsArray =
    types?.some((type) => type.startsWith('array<')) ?? false;
  if (typeof val === 'string' && (acceptsObject || acceptsArray)) {
    const trimmed = val.trim();
    if (!trimmed) return val;
    try {
      const parsed = JSON.parse(trimmed);
      const parsedIsArray = Array.isArray(parsed);
      const parsedIsObject =
        typeof parsed === 'object' && parsed !== null && !parsedIsArray;
      if (parsedIsArray && !acceptsArray) {
        return val;
      }
      if (parsedIsObject && !acceptsObject) {
        return val;
      }
      if (typeof parsed === 'string' && !acceptsString) {
        return val;
      }
      if (!parsedIsArray && !parsedIsObject && typeof parsed !== 'string') {
        return val;
      }
      return parsed;
    } catch {
      return val;
    }
  }
  return val;
}

export function isEmailRelatedTask(
  definition?: Pick<TaskDefinition, 'name'>
): boolean {
  return ['oauth2-email-read', 'oauth2-email-send'].includes(
    definition?.name ?? ''
  );
}

export function enhanceConfigDescriptionForEmailTask(
  configDesc: Record<string, InspectorFieldDescription> | undefined,
  definition: Pick<TaskDefinition, 'name'> | undefined,
  emailAccounts: EmailAccount[] | undefined
): Record<string, InspectorFieldDescription> | undefined {
  if (!configDesc || !isEmailRelatedTask(definition)) {
    return configDesc;
  }

  const enhanced = { ...configDesc };
  if (enhanced.emailAddress && emailAccounts) {
    enhanced.emailAddress = {
      ...enhanced.emailAddress,
      options: emailAccounts.map((account) => account.email),
      defaultValue:
        emailAccounts.length > 0
          ? emailAccounts[0].email
          : enhanced.emailAddress.defaultValue,
    };
  }

  return enhanced;
}

export function shouldUseMultiline(
  desc: InspectorFieldDescription | undefined,
  value: unknown
): boolean {
  if (typeof value === 'string' && value.includes('\n')) return true;
  if (
    typeof desc?.placeholder === 'string' &&
    desc.placeholder.includes('\n')
  ) {
    return true;
  }
  const rows = desc?.rows ?? desc?.typeOptions?.rows;
  if (typeof rows === 'number' && rows > 1) return true;
  return false;
}

function normalizeVisibilityValue(value: unknown): unknown {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed && !Number.isNaN(Number(trimmed))) {
      return Number(trimmed);
    }
  }
  return value;
}

export function isFieldVisible(
  desc: InspectorFieldDescription,
  config: Record<string, unknown> | undefined
): boolean {
  const visibility = desc.visibility;
  if (!visibility || visibility.length === 0) return true;

  return visibility.some((rule) => {
    if (rule.show) {
      for (const [key, allowed] of Object.entries(rule.show)) {
        const allowedValues = Array.isArray(allowed) ? allowed : [allowed];
        const actual = normalizeVisibilityValue(config?.[key]);
        if (!allowedValues.some((value) => value === actual)) return false;
      }
    }
    if (rule.hide) {
      for (const [key, disallowed] of Object.entries(rule.hide)) {
        const disallowedValues = Array.isArray(disallowed)
          ? disallowed
          : [disallowed];
        const actual = normalizeVisibilityValue(config?.[key]);
        if (disallowedValues.some((value) => value === actual)) return false;
      }
    }
    return true;
  });
}

export function filterConfigFields(
  configDesc: Record<string, InspectorFieldDescription> | undefined,
  config: Record<string, unknown> | undefined
): Record<string, InspectorFieldDescription> | undefined {
  if (!configDesc) return configDesc;

  const filtered: Record<string, InspectorFieldDescription> = {};
  for (const [key, desc] of Object.entries(configDesc)) {
    if (isFieldVisible(desc, config)) {
      filtered[key] = desc;
    }
  }

  return filtered;
}

export function getConfigEntries(
  configDesc: Record<string, InspectorFieldDescription> | undefined
): InspectorFieldEntries {
  if (!configDesc) return [];
  return Object.entries(configDesc);
}

export function resolveConfigRawValue(
  config: Record<string, unknown> | undefined,
  key: string,
  description: InspectorFieldDescription
): unknown {
  return (
    config?.[key] ??
    description.defaultValue ??
    (description.types?.includes('boolean') ? false : '')
  );
}

export function normalizeConfigDisplayValue(
  value: unknown,
  types?: string[]
): unknown {
  if (Array.isArray(value) && value.length === 0 && types?.includes('string')) {
    return '';
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
}

export function isStackedField(
  description: InspectorFieldDescription
): boolean {
  return (
    description.component === 'textarea' || description.component === 'code'
  );
}

export function getOptionValue(option: InspectorFieldOption): string {
  if (typeof option === 'object' && option !== null && 'value' in option) {
    return String(option.value);
  }
  return String(option);
}

export function getOptionLabel(option: InspectorFieldOption): string {
  if (typeof option === 'object' && option !== null && 'label' in option) {
    return String(option.label);
  }
  return String(option);
}

export function getOptionDescription(
  option: InspectorFieldOption
): string | undefined {
  if (
    typeof option === 'object' &&
    option !== null &&
    'description' in option &&
    option.description
  ) {
    return String(option.description);
  }
  return undefined;
}

export function getPickerPlaceholderLabel(
  fallbackLabel: string,
  value: unknown,
  options: InspectorFieldOption[]
): string {
  const stringValue = value == null ? '' : String(value);
  if (stringValue !== '') return fallbackLabel;

  const emptyOption = options.find((option) => getOptionValue(option) === '');
  const emptyLabel = emptyOption ? getOptionLabel(emptyOption) : '';
  return emptyLabel || fallbackLabel;
}

export function resolveEditorRows(
  description: { rows?: number; maxRows?: number },
  value: unknown,
  defaultRows: number,
  defaultMaxRows: number
): number {
  const minRows =
    typeof description.rows === 'number' && description.rows > 0
      ? description.rows
      : defaultRows;
  const maxRows =
    typeof description.maxRows === 'number' && description.maxRows >= minRows
      ? description.maxRows
      : Math.max(defaultMaxRows, minRows);
  const lineCount =
    typeof value === 'string' && value.length > 0
      ? value.split('\n').length
      : minRows;

  return Math.min(Math.max(lineCount, minRows), maxRows);
}

export function resolveTextareaRows(
  description: { rows?: number; maxRows?: number },
  value: unknown
): number {
  return resolveEditorRows(description, value, 4, 12);
}
